import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Requests from "@/pages/Requests";
import Bids from "@/pages/Bids";
import PriceBook from "@/pages/PriceBook";
import Crew from "@/pages/Crew";
import Messages from "@/pages/Messages";
import MapView from "@/pages/MapView";
import PublicBid from "@/pages/PublicBid";
import PublicPhotos from "@/pages/PublicPhotos";
import PublicRequest from "@/pages/PublicRequest";
import HowItWorks from "@/pages/HowItWorks";
import ContractorHub from "@/pages/ContractorHub";
import Clients from "@/pages/Clients";
import Profile from "@/pages/Profile";
import AdminGuide from "@/pages/AdminGuide";
import Training from "@/pages/Training";
import Pricing from "@/pages/Pricing";
import Reports from "@/pages/Reports";
import Checklists from "@/pages/Checklists";
import PayRates from "@/pages/PayRates";
import PublicCoverage from "@/pages/PublicCoverage";

// Stable role arrays (module scope) so route props don't allocate new arrays each render
const R_ADMIN = ["admin"];
const R_ADMIN_CLIENT = ["admin", "client"];
const R_CREW = ["crew"];
const R_CLIENT = ["client"];

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
      <div className="font-mono text-[#FF5722] uppercase tracking-widest animate-pulse">Loading…</div>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { applyAuth } = useAuth();
  const processed = useRef(false);
  useEffect(() => {
    // Guard against React StrictMode double-invoke: session_id is single-use.
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) { navigate("/login"); return; }
    (async () => {
      try {
        const intended = sessionStorage.getItem("ss_intended_role") || undefined;
        const { data } = await api.post("/auth/google/session", { session_id: sid, intended_role: intended });
        sessionStorage.removeItem("ss_intended_role");
        window.history.replaceState({}, "", "/");
        applyAuth(data.token, data.user);
        toast.success(`Welcome, ${data.user.name}`);
        navigate("/");
      } catch (err) {
        console.error("Google sign-in failed:", err);
        toast.error("Google sign-in failed. Please try again.");
        navigate("/login");
      }
    })();
  }, []); // eslint-disable-line
  return <Loading />;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "admin") return <Navigate to="/dashboard" replace />;
  if (user?.role === "client") return <Navigate to="/requests" replace />;
  return <Navigate to="/jobs" replace />;
}

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <HomeRedirect />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/bid/:token" element={<PublicBid />} />
      <Route path="/photos/:token" element={<PublicPhotos />} />
      <Route path="/request" element={<PublicRequest />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/" element={<Protected><HomeRedirect /></Protected>} />
      <Route path="/dashboard" element={<Protected roles={R_ADMIN}><Dashboard /></Protected>} />
      <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
      <Route path="/map" element={<Protected roles={R_ADMIN}><MapView /></Protected>} />
      <Route path="/archived" element={<Protected roles={R_ADMIN}><Jobs archived /></Protected>} />
      <Route path="/jobs/:id" element={<Protected><JobDetail /></Protected>} />
      <Route path="/requests" element={<Protected roles={R_ADMIN_CLIENT}><Requests /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/bids" element={<Protected roles={R_ADMIN}><Bids /></Protected>} />
      <Route path="/pricebook" element={<Protected roles={["admin", "client", "crew"]}><PriceBook /></Protected>} />
      <Route path="/contractor" element={<Protected roles={R_CREW}><ContractorHub /></Protected>} />
      <Route path="/crew" element={<Protected roles={R_ADMIN}><Crew /></Protected>} />
      <Route path="/clients" element={<Protected roles={R_ADMIN}><Clients /></Protected>} />
      <Route path="/guide" element={<Protected roles={R_ADMIN}><AdminGuide /></Protected>} />
      <Route path="/training" element={<Protected roles={["admin", "crew"]}><Training /></Protected>} />
      <Route path="/pricing" element={<Protected roles={R_ADMIN}><Pricing /></Protected>} />
      <Route path="/reports" element={<Protected roles={R_ADMIN}><Reports /></Protected>} />
      <Route path="/checklists" element={<Protected roles={R_ADMIN}><Checklists /></Protected>} />
      <Route path="/pay-rates" element={<Protected roles={R_CREW}><PayRates /></Protected>} />
      <Route path="/coverage/:token" element={<PublicCoverage />} />
      <Route path="/profile" element={<Protected roles={["client", "crew"]}><Profile /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const TOAST_OPTIONS = { style: { background: "#141417", border: "2px solid #FF5722", color: "#fff", borderRadius: 0 } };

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster theme="dark" position="top-right" toastOptions={TOAST_OPTIONS} />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
